import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import CommentModal from "../components/common/CommentModal";
import ActionMenu from "../components/common/ActionMenu";
import {
  MdAdd,
  MdEdit,
  MdDelete,
  MdComment,
  MdFilterList,
  MdViewKanban,
  MdTableRows,
  MdVisibility,
  MdTask,
  MdCalendarToday,
  MdPerson,
  MdPersonAdd,
  MdFlag,
  MdSwapHoriz,
  MdHourglassEmpty,
  MdTrendingUp,
  MdRateReview,
  MdAttachFile,
  MdPeople,
} from "react-icons/md";
import api from "../services/api";
import socket from "../services/socket";
import {
  insertMentionAtCursor,
  toMentionDisplayText,
  parseMentionsOrdered,
  toBackendMentionFormat,
} from "../utils/mentionUtils";
import { useAuth } from "../context/AuthContext";
import { canMarkTaskComplete } from "../utils/taskPermissions";
import { handlePendingDoneRequestResponse } from "../utils/taskUpdateHelpers";
import toast from "react-hot-toast";
import { format, isBefore, addDays } from "date-fns";
import "./MyTasks.css";

const API_URL =
  process.env.NODE_ENV === "production"
    ? process.env.REACT_APP_API_URL || window.location.origin
    : "http://localhost:5000";

const NEW_TASK_HOURS = 72; // show "New" badge if assigned within this many hours and not yet opened
const sameId = (a, b) =>
  a != null && b != null && String(a) === String(b);
/** Include tasks where the user is creator in /tasks list and socket updates (admin: assigned/co-only). */
const includeCreatorScopeInRealtime = (role) => role !== "admin";

/** True if user is primary assignee, in assignedUsers[], or co-assignee. */
const isAssignedToUser = (task, userId) => {
  if (!task || userId == null) return false;
  if (sameId(task.assignedTo?._id ?? task.assignedTo, userId)) return true;
  if (sameId(task.coAssignedTo?._id ?? task.coAssignedTo, userId)) return true;
  if (Array.isArray(task.assignedUsers)) {
    return task.assignedUsers.some((u) =>
      sameId(u?._id ?? u, userId),
    );
  }
  return false;
};

const isNewTask = (task, currentUserId) => {
  if (!isAssignedToUser(task, currentUserId)) return false;
  if (task.seenByAssigneeAt) return false; // user opened task detail – badge disappears
  const assignedAt = task.assignedAt ? new Date(task.assignedAt) : null;
  if (!assignedAt) return false;
  const hours = (Date.now() - assignedAt.getTime()) / (1000 * 60 * 60);
  return hours <= NEW_TASK_HOURS;
};

// Note: completed (done) tasks are never auto-hidden.

const KANBAN_COLS = [
  {
    key: "todo",
    label: "To Do",
    color: "#64748b",
    bg: "#f1f5f9",
    icon: MdHourglassEmpty,
  },
  {
    key: "in-progress",
    label: "In Progress",
    color: "#2563eb",
    bg: "#dbeafe",
    icon: MdTrendingUp,
  },
  {
    key: "review",
    label: "Review",
    color: "#d97706",
    bg: "#fef3c7",
    icon: MdRateReview,
  },
];

const getInitials = (name) =>
  name
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
const COLORS = [
  "#6366f1",
  "#f59e0b",
  "#10b981",
  "#ef4444",
  "#3b82f6",
  "#8b5cf6",
];
const avatarColor = (name) =>
  COLORS[(name?.charCodeAt(0) || 0) % COLORS.length];

const UserAvatar = ({ user, size = 28 }) => {
  if (!user) return null;
  const imgSrc = user.avatar
    ? user.avatar.startsWith("http")
      ? user.avatar
      : `${API_URL}${user.avatar}`
    : null;
  return (
    <div
      className="avatar"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.3,
        background: imgSrc ? "transparent" : avatarColor(user.name),
        flexShrink: 0,
      }}
      title={user.name}
    >
      {imgSrc ? (
        <img
          src={imgSrc}
          alt={user.name}
          onError={(e) => {
            e.target.style.display = "none";
            e.target.parentNode.textContent = getInitials(user.name);
          }}
        />
      ) : (
        getInitials(user.name)
      )}
    </div>
  );
};

// ─── Task Modal ───────────────────────────────────────────────
const TaskModal = ({ task, users, currentUser, onClose, onSaved }) => {
  const [form, setForm] = useState(
    task
      ? {
          title: task.title,
          description: toMentionDisplayText(task.description || ""),
          assignedTo: Array.isArray(task.assignedUsers) && task.assignedUsers.length
            ? task.assignedUsers.map((u) => (u?._id ? u._id : u)).filter(Boolean)
            : task.assignedTo?._id
              ? [task.assignedTo._id]
              : [],
          coAssignedTo: task.coAssignedTo?._id || "",
          status: task.status,
          priority: task.priority,
          deadline: task.deadline ? task.deadline.slice(0, 10) : "",
          tags: Array.isArray(task.tags) ? task.tags.join(", ") : "",
        }
      : {
          title: "",
          description: "",
          assignedTo: [currentUser._id],
          coAssignedTo: "",
          status: "todo",
          priority: "medium",
          deadline: "",
          tags: "",
        },
  );
  const [loading, setLoading] = useState(false);
  const [assignSearch, setAssignSearch] = useState("");
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionStart, setMentionStart] = useState(-1);
  const [mentionSearch, setMentionSearch] = useState("");
  const descRef = React.useRef(null);
  const descMentionListRef = React.useRef([]);
  const createAttachInputRef = React.useRef(null);
  const [pendingFiles, setPendingFiles] = useState([]);

  React.useEffect(() => {
    if (task?.description)
      descMentionListRef.current = parseMentionsOrdered(task.description);
    else descMentionListRef.current = [];
  }, [task?._id, task?.description]);

  React.useEffect(() => {
    if (task) setPendingFiles([]);
  }, [task?._id, task]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.deadline) {
      toast.error("Deadline is required");
      return;
    }
    const uid = currentUser?._id ?? currentUser?.id;
    if (
      task &&
      form.status === "done" &&
      task.status !== "done" &&
      !canMarkTaskComplete(uid, task)
    ) {
      toast.error("Only the task creator or assigned users can mark this task as complete");
      return;
    }
    setLoading(true);
    try {
      const descriptionForApi = toBackendMentionFormat(
        form.description,
        descMentionListRef.current,
      );
      const payload = {
        ...form,
        description: descriptionForApi,
        tags:
          typeof form.tags === "string"
            ? form.tags
                .split(",")
                .map((t) => t.trim())
                .filter(Boolean)
            : form.tags,
        assignedUsers: form.assignedTo,
        assignedTo: form.assignedTo[0] || currentUser._id,
        coAssignedTo: form.coAssignedTo || null,
        deadline: form.deadline,
      };
      if (task) {
        const { data } = await api.put(`/tasks/${task._id}`, payload);
        if (!handlePendingDoneRequestResponse(data, toast)) {
          toast.success("Task updated");
        }
      } else {
        const { data } = await api.post("/tasks", payload);
        const createdId = data.data?._id;
        const files = pendingFiles;
        const uploadErrors = [];
        if (files.length > 0 && createdId) {
          for (const file of files) {
            try {
              const fd = new FormData();
              fd.append("file", file);
              await api.post(`/tasks/${createdId}/attachments`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
              });
            } catch {
              uploadErrors.push(file.name);
            }
          }
        }
        if (uploadErrors.length === 0) {
          toast.success(
            files.length > 0
              ? "Task created with attachments"
              : "Task created",
          );
        } else if (uploadErrors.length === files.length) {
          toast.success("Task created");
          toast.error(
            "Attachments failed to upload. Add files from task details.",
          );
        } else {
          toast.success("Task created");
          toast.error(
            `Some files failed: ${uploadErrors.join(", ")}. Add the rest from task details.`,
          );
        }
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error saving task");
    } finally {
      setLoading(false);
    }
  };

  const handleDescChange = (e) => {
    const val = e.target.value;
    const pos = e.target.selectionStart ?? val.length;
    setForm({ ...form, description: val });
    const beforeCursor = val.slice(0, pos);
    const atIdx = beforeCursor.lastIndexOf("@");
    if (atIdx !== -1 && (atIdx === 0 || /\s/.test(val[atIdx - 1]))) {
      setMentionStart(atIdx);
      setMentionSearch(beforeCursor.slice(atIdx + 1).toLowerCase());
      setMentionOpen(true);
    } else {
      setMentionOpen(false);
    }
  };

  const handleSelectDescMention = (u) => {
    const cursorPos =
      descRef.current?.selectionStart ?? form.description.length;
    const countBefore = (
      form.description.slice(0, mentionStart).match(/@/g) || []
    ).length;
    descMentionListRef.current.splice(countBefore, 0, {
      userId: u._id,
      displayName: u.name,
    });
    const { newValue, newPos } = insertMentionAtCursor(
      form.description,
      mentionStart,
      cursorPos,
      u._id,
      u.name,
    );
    setForm({ ...form, description: newValue });
    setMentionOpen(false);
    setTimeout(() => {
      descRef.current?.focus();
      descRef.current?.setSelectionRange(newPos, newPos);
    }, 0);
  };

  const mentionFiltered = mentionSearch
    ? users.filter((u) => (u.name || "").toLowerCase().includes(mentionSearch))
    : users;
  const assignFiltered = assignSearch
    ? users.filter((u) =>
        `${u.name || ""} ${u.email || ""}`.toLowerCase().includes(assignSearch.toLowerCase()),
      )
    : users;

  const me = currentUser?._id ?? currentUser?.id;
  const showDoneStatusOption =
    !task ||
    canMarkTaskComplete(me, task) ||
    (task.status || "") === "done";

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{task ? "Edit Task" : "New Task"}</h3>
          <button className="btn-icon" onClick={onClose}>
            ✕
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Title</label>
              <input
                className="form-control"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Task title"
                required
              />
            </div>
            <div className="form-group" style={{ position: "relative" }}>
              <label className="form-label">Description</label>
              <textarea
                ref={descRef}
                className="form-control"
                rows={3}
                value={form.description}
                onChange={handleDescChange}
                onBlur={() => setTimeout(() => setMentionOpen(false), 200)}
                placeholder="Optional description (type @ to mention a user)"
              />
              {mentionOpen && mentionFiltered.length > 0 && (
                <ul
                  className="mention-dropdown mention-dropdown-below"
                  role="listbox"
                >
                  {mentionFiltered.map((u) => (
                    <li
                      key={u._id}
                      role="option"
                      className="mention-dropdown-item"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleSelectDescMention(u);
                      }}
                    >
                      <UserAvatar user={u} size={28} />
                      <span>{u.name}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 14,
                marginBottom: 18,
              }}
            >
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Assign To (Multiple)</label>
                <div className="form-control" style={{ maxHeight: 190, overflow: "auto" }}>
                  <input
                    className="form-control"
                    style={{ marginBottom: 8 }}
                    placeholder="Search user..."
                    value={assignSearch}
                    onChange={(e) => setAssignSearch(e.target.value)}
                  />
                  {assignFiltered.map((u) => {
                    const checked = form.assignedTo.includes(u._id);
                    return (
                      <label
                        key={u._id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "6px 2px",
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            setForm((f) => {
                              const next = isChecked
                                ? [...f.assignedTo, u._id]
                                : f.assignedTo.filter((id) => id !== u._id);
                              return {
                                ...f,
                                assignedTo: [...new Set(next)],
                                coAssignedTo:
                                  f.coAssignedTo && next.includes(f.coAssignedTo)
                                    ? ""
                                    : f.coAssignedTo,
                              };
                            });
                          }}
                        />
                        <span>
                          {u.name}
                          {u.email ? ` (${u.email})` : ""}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">
                  Co-Assign To
                  <span
                    style={{
                      marginLeft: 6,
                      fontSize: "0.68rem",
                      fontWeight: 500,
                      color: "var(--text-muted)",
                      textTransform: "none",
                      letterSpacing: 0,
                    }}
                  >
                    (assistant)
                  </span>
                </label>
                <select
                  className="form-control"
                  value={form.coAssignedTo}
                  onChange={(e) => {
                    const v = e.target.value;
                    setForm((f) => ({
                      ...f,
                      coAssignedTo: v,
                      assignedTo: f.assignedTo === v ? "" : f.assignedTo,
                    }));
                  }}
                >
                  <option value="">— None —</option>
                  {users
                    .filter((u) => !form.assignedTo.includes(u._id))
                    .map((u) => (
                      <option key={u._id} value={u._id}>
                        {u.name}
                        {u.email ? ` (${u.email})` : ""}
                      </option>
                    ))}
                </select>
              </div>
            </div>
            <div className="grid grid-2">
              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  className="form-control"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  title={
                    task &&
                    !canMarkTaskComplete(me, task) &&
                    (task.status || "") !== "done"
                      ? "Only the creator or assigned users can mark this task complete"
                      : undefined
                  }
                >
                  <option value="todo">To Do</option>
                  <option value="in-progress">In Progress</option>
                  <option value="review">Review</option>
                  {showDoneStatusOption && <option value="done">Done</option>}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Priority</label>
                <select
                  className="form-control"
                  value={form.priority}
                  onChange={(e) =>
                    setForm({ ...form, priority: e.target.value })
                  }
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Deadline</label>
              <input
                className="form-control"
                type="date"
                value={form.deadline}
                onChange={(e) => setForm({ ...form, deadline: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Tags (comma-separated)</label>
              <input
                className="form-control"
                value={form.tags}
                onChange={(e) => setForm({ ...form, tags: e.target.value })}
                placeholder="design, bug, feature"
              />
            </div>
            {!task && (
              <div className="form-group">
                <label className="form-label">
                  Related files{" "}
                  <span
                    style={{
                      fontWeight: 400,
                      color: "var(--text-muted)",
                    }}
                  >
                    (optional, max 2)
                  </span>
                </label>
                <input
                  ref={createAttachInputRef}
                  type="file"
                  multiple
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const picked = Array.from(e.target.files || []);
                    setPendingFiles((prev) => {
                      const next = [...prev];
                      for (const f of picked) {
                        if (next.length >= 2) break;
                        next.push(f);
                      }
                      if (prev.length + picked.length > 2) {
                        toast.error("You can attach at most 2 files.");
                      }
                      return next;
                    });
                    e.target.value = "";
                  }}
                />
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 8,
                    alignItems: "center",
                  }}
                >
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    disabled={pendingFiles.length >= 2}
                    onClick={() => createAttachInputRef.current?.click()}
                  >
                    <MdAttachFile
                      size={16}
                      style={{ verticalAlign: "text-bottom" }}
                    />{" "}
                    Add file
                  </button>
                  <span
                    style={{
                      fontSize: "0.75rem",
                      color: "var(--text-muted)",
                    }}
                  >
                    PDF, images, Word, Excel, CSV, TXT, ZIP · 20 MB each
                  </span>
                </div>
                {pendingFiles.length > 0 && (
                  <ul
                    style={{
                      margin: "8px 0 0",
                      paddingLeft: 18,
                      fontSize: "0.85rem",
                    }}
                  >
                    {pendingFiles.map((f, i) => (
                      <li
                        key={`${f.name}-${i}-${f.size}`}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span title={f.name}>{f.name}</span>
                        <button
                          type="button"
                          className="btn-icon"
                          aria-label="Remove file"
                          onClick={() =>
                            setPendingFiles((prev) =>
                              prev.filter((_, j) => j !== i),
                            )
                          }
                        >
                          ✕
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? "Saving..." : task ? "Update Task" : "Create Task"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Quick Edit Modal (Assign / Priority / Status) ─────────────
const QuickEditModal = ({
  task,
  type,
  users,
  currentUser,
  onClose,
  onSaved,
}) => {
  const [value, setValue] = useState(
    type === "assign"
      ? Array.isArray(task?.assignedUsers) && task.assignedUsers.length
        ? task.assignedUsers
            .map((u) => (u?._id ? u._id : u))
            .filter(Boolean)
        : task?.assignedTo?._id
          ? [task.assignedTo._id]
          : []
      : task?.[type] || "",
  );
  const [loading, setLoading] = useState(false);
  const [assignSearch, setAssignSearch] = useState("");
  const assignFiltered = assignSearch
    ? users.filter((u) =>
        `${u.name || ""} ${u.email || ""}`.toLowerCase().includes(assignSearch.toLowerCase()),
      )
    : users;

  useEffect(() => {
    setValue(
      type === "assign"
        ? Array.isArray(task?.assignedUsers) && task.assignedUsers.length
          ? task.assignedUsers
              .map((u) => (u?._id ? u._id : u))
              .filter(Boolean)
          : task?.assignedTo?._id
            ? [task.assignedTo._id]
            : []
        : task?.[type] || "",
    );
  }, [task, type]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!task) return;
    setLoading(true);
    try {
      const payload =
        type === "assign"
          ? {
              assignedUsers: value,
              assignedTo: value[0] || currentUser._id,
            }
          : { [type]: value };
      const { data } = await api.put(`/tasks/${task._id}`, payload);
      if (
        type === "status" &&
        handlePendingDoneRequestResponse(data, toast)
      ) {
        onSaved();
        onClose();
        return;
      }
      toast.success(
        type === "assign"
          ? "Assignee updated"
          : type === "priority"
            ? "Priority updated"
            : "Status updated",
      );
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Update failed");
    } finally {
      setLoading(false);
    }
  };

  const labels = {
    assign: "Assign to",
    priority: "Priority",
    status: "Status",
  };
  const quickMe = currentUser?._id ?? currentUser?.id;
  const options =
    type === "assign"
      ? users?.map((u) => ({
          value: u._id,
          label: `${u.name} (${u.email})`,
        })) || []
      : type === "priority"
        ? [
            { value: "low", label: "Low" },
            { value: "medium", label: "Medium" },
            { value: "high", label: "High" },
            { value: "critical", label: "Critical" },
          ]
        : [
            { value: "todo", label: "To Do" },
            { value: "in-progress", label: "In Progress" },
            { value: "review", label: "Review" },
            ...(canMarkTaskComplete(quickMe, task) ||
            (task?.status || "") === "done"
              ? [{ value: "done", label: "Done" }]
              : []),
          ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-sm" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Change {labels[type]}</h3>
          <button type="button" className="btn-icon" onClick={onClose}>
            ✕
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">{labels[type]}</label>
              {type === "assign" ? (
                <div
                  className="form-control"
                  style={{ maxHeight: 230, overflow: "auto" }}
                >
                  <input
                    className="form-control"
                    style={{ marginBottom: 8 }}
                    placeholder="Search user..."
                    value={assignSearch}
                    onChange={(e) => setAssignSearch(e.target.value)}
                  />
                  {assignFiltered.map((u) => {
                    const checked = value.includes(u._id);
                    return (
                      <label
                        key={u._id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "6px 2px",
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            setValue((prev) =>
                              isChecked
                                ? [...new Set([...prev, u._id])]
                                : prev.filter((id) => id !== u._id),
                            );
                          }}
                        />
                        <span>{u.name}{u.email ? ` (${u.email})` : ""}</span>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <select
                  className="form-control"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                >
                  {options.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Kanban Card ──────────────────────────────────────────────
const TaskCard = ({
  task,
  canEdit,
  canDel,
  isNew,
  onEdit,
  onDelete,
  onComment,
  onViewDetails,
  onAssign,
  onPriority,
  onStatus,
  onAttach,
}) => {
  const assignedUsers =
    Array.isArray(task.assignedUsers) && task.assignedUsers.length
      ? task.assignedUsers
      : task.assignedTo
        ? [task.assignedTo]
        : [];
  const displayAssignedUsers = assignedUsers.filter(
    (assignedUser) => !sameId(assignedUser?._id, task.createdBy?._id),
  );
  const showAssignedBy =
    !!task.createdBy;

  const isOverdue =
    task.deadline &&
    new Date(task.deadline) < new Date() &&
    !["done", "completed", "closed"].includes(
      (task.status || "").toLowerCase(),
    );

  const menuItems = [
    {
      label: "View task details",
      icon: <MdVisibility size={16} />,
      onClick: () => onViewDetails(task),
    },
    ...(canEdit
      ? [
          {
            label: "Edit task",
            icon: <MdEdit size={16} />,
            onClick: () => onEdit(task),
          },
          {
            label: "Assign to user",
            icon: <MdPerson size={16} />,
            onClick: () => onAssign(task),
          },
          {
            label: "Change priority",
            icon: <MdFlag size={16} />,
            onClick: () => onPriority(task),
          },
          {
            label: "Change status",
            icon: <MdSwapHoriz size={16} />,
            onClick: () => onStatus(task),
          },
          {
            label: "Attach file",
            icon: <MdAttachFile size={16} />,
            onClick: () => onAttach(task),
          },
        ]
      : []),
    {
      label: "Add comment",
      onClick: () => onComment(task),
    },
    {
      label: "View comments",
      onClick: () => onComment(task),
    },
    "divider",
    ...(canDel
      ? [
          {
            label: "Delete task",
            icon: <MdDelete size={16} />,
            onClick: () => onDelete(task._id),
            danger: true,
          },
        ]
      : []),
  ].filter(Boolean);

  return (
    <div className={`kanban-card ${isOverdue ? "kanban-card-overdue" : ""}`}>
      <div className="card-content-area">
        <div className="card-title-row">
          <div
            className="card-title card-title-clickable"
            onClick={() => onViewDetails(task)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && onViewDetails(task)}
          >
            {task.title}
          </div>
          <div className="card-actions">
            <ActionMenu items={menuItems} align="right" />
          </div>
        </div>

        {task.description && (
          <p className="card-description">{task.description}</p>
        )}

        <div className="card-meta-row">
          {isNew && (
            <span className="new-task-badge">
              <span className="badge-dot" />
              New
            </span>
          )}
          <span className={`card-priority-badge badge-${task.priority}`}>
            {task.priority}
          </span>
          {displayAssignedUsers.length > 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
                width: "100%",
              }}
            >
              {displayAssignedUsers.slice(0, 3).map((assignedUser) => (
                <div key={assignedUser?._id || assignedUser} className="card-assignee">
                  <UserAvatar user={assignedUser} size={22} />
                  <span>{assignedUser?.name || "User"}</span>
                </div>
              ))}
              {displayAssignedUsers.length > 3 && (
                <span
                  style={{
                    fontSize: "0.72rem",
                    color: "var(--text-muted)",
                    fontWeight: 600,
                  }}
                >
                  +{displayAssignedUsers.length - 3} more
                </span>
              )}
            </div>
          )}
        </div>

        {task.coAssignedTo && (
          <div className="card-co-assignee-wrap">
            <span className="card-co-assignee-label">
              <MdPeople size={14} /> Co-Assignee
            </span>
            <div className="card-assigned-by">
              <UserAvatar user={task.coAssignedTo} size={24} />
              <span className="card-assigned-by-name">
                {task.coAssignedTo.name}
              </span>
            </div>
          </div>
        )}

        {showAssignedBy && (
          <div
            className="card-assigned-by-wrap"
            title={`Assigned by ${task.createdBy.name}`}
          >
            <span className="card-assigned-by-label">
              <MdPersonAdd size={14} /> Assigned by
            </span>
            <div className="card-assigned-by">
              <UserAvatar user={task.createdBy} size={24} />
              <span className="card-assigned-by-name">
                {task.createdBy.name}
              </span>
            </div>
          </div>
        )}

        {task.deadline && (
          <div className="card-deadline">
            <MdCalendarToday className="date-icon" size={14} />
            {format(new Date(task.deadline), "MMM d, yyyy")}
          </div>
        )}

        {task.tags?.length > 0 && (
          <div className="card-tags">
            {task.tags.map((tag) => (
              <span key={tag} className="tag">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────
const DUE_SOON_DAYS = 3;
const isOverdueTask = (task) => {
  if (!task?.deadline) return false;
  const status = (task.status || "").toLowerCase();
  if (["done", "completed", "closed"].includes(status)) return false;
  return isBefore(new Date(task.deadline), new Date());
};
const isDueSoonTask = (task) => {
  if (!task?.deadline) return false;
  const status = (task.status || "").toLowerCase();
  if (["done", "completed", "closed"].includes(status)) return false;
  const now = new Date();
  const dl = new Date(task.deadline);
  return !isBefore(dl, now) && isBefore(dl, addDays(now, DUE_SOON_DAYS));
};

const isTerminalTaskStatus = (task) =>
  ["done", "completed", "closed"].includes(
    (task?.status || "").toLowerCase(),
  );

const MyTasks = ({
  forceView = null, // "kanban" | "list" | null
  initialStatus = "",
  lockedStatus = false,
  title = "My Tasks",
  hideNewTaskButton = false,
  hideCommentIcons = false,
  showCompletedOn = false,
} = {}) => {
  const { user, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dateFilter = searchParams.get("filter"); // 'overdue' | 'due-soon' | null
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(forceView || "kanban");
  const [filter, setFilter] = useState({
    priority: "",
    status: initialStatus || "",
    assignee: "",
    dueFilter: "", // "" | "overdue" | "due-soon"
    search: "",
  });
  const [showModal, setShowModal] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [commentTask, setCommentTask] = useState(null);
  const [quickEdit, setQuickEdit] = useState(null); // { task, type: 'assign'|'priority'|'status' }
  const [attachTask, setAttachTask] = useState(null);
  const attachInputRef = React.useRef(null);
  /** Super Admin: which task bucket is shown (tabs). */
  const [superAdminTaskTab, setSuperAdminTaskTab] = useState("assigned");

  // Keep view/status forced when used as a wrapper page (Completed Tasks)
  useEffect(() => {
    if (forceView) setView(forceView);
  }, [forceView]);
  useEffect(() => {
    if (initialStatus) setFilter((f) => ({ ...f, status: initialStatus }));
  }, [initialStatus]);

  const fetchTasks = useCallback(async () => {
    const { data } = await api.get("/tasks");
    setTasks(data.data);
  }, []);

  useEffect(() => {
    Promise.all([api.get("/tasks"), api.get("/users/list")])
      .then(([t, u]) => {
        setTasks(t.data.data);
        setUsers(u.data.data);
      })
      .finally(() => setLoading(false));

    const onCreated = (t) =>
      setTasks((p) => {
        if (!t?._id) return p;
        const uid = user?._id ?? user?.id;
        const showCreator = includeCreatorScopeInRealtime(user?.role);
        const relevant =
          (showCreator && sameId(t.createdBy?._id, uid)) ||
          isAssignedToUser(t, uid);
        if (!relevant) return p;
        if (p.some((x) => sameId(x?._id, t._id))) {
          return p.map((x) => (sameId(x?._id, t._id) ? t : x));
        }
        return [t, ...p];
      });
    const onUpdated = (t) =>
      setTasks((p) => {
        const uid = user?._id ?? user?.id;
        const showCreator = includeCreatorScopeInRealtime(user?.role);
        const relevant =
          (showCreator && sameId(t.createdBy?._id, uid)) ||
          isAssignedToUser(t, uid);
        if (p.some((x) => sameId(x._id, t._id))) {
          return relevant
            ? p.map((x) => (sameId(x._id, t._id) ? t : x))
            : p.filter((x) => !sameId(x._id, t._id));
        }
        return relevant ? [t, ...p] : p;
      });
    const onDeleted = (id) =>
      setTasks((p) => p.filter((x) => !sameId(x._id, id)));
    const onCommented = (t) =>
      setTasks((p) => p.map((x) => (sameId(x._id, t._id) ? t : x)));

    socket.on("taskCreated", onCreated);
    socket.on("taskUpdated", onUpdated);
    socket.on("taskDeleted", onDeleted);
    socket.on("taskCommented", onCommented);
    return () => {
      socket.off("taskCreated", onCreated);
      socket.off("taskUpdated", onUpdated);
      socket.off("taskDeleted", onDeleted);
      socket.off("taskCommented", onCommented);
    };
  }, [user?._id, user?.id]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await api.delete(`/tasks/${id}`);
      // Optimistically remove from local state so UI updates immediately
      setTasks((prev) => prev.filter((t) => t._id !== id));
      toast.success("Task deleted");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to delete task");
    }
  };

  const handleAttachClick = (task) => {
    setAttachTask(task);
    if (attachInputRef.current) {
      attachInputRef.current.value = "";
      attachInputRef.current.click();
    }
  };

  const handleAttachChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !attachTask) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/tasks/${attachTask._id}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Attachment uploaded");
      await fetchTasks();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to upload attachment");
    } finally {
      setAttachTask(null);
      if (attachInputRef.current) attachInputRef.current.value = "";
    }
  };

  const me = user?._id ?? user?.id;
  const canEdit = (t) =>
    sameId(t.createdBy?._id, me) ||
    isAssignedToUser(t, me) ||
    user?.role === "admin";
  const canDel = (t) => {
    if (sameId(t.createdBy?._id, me)) return true;
    if (user?.role === "admin" || user?.role === "super_admin") return true;
    const isRecurringTask = !!t?.scheduledFrom;
    return isRecurringTask && isAssignedToUser(t, me);
  };

  const taskMatchesAssigneeFilter = (t, assigneeId) => {
    if (assigneeId === "unassigned") {
      const hasPrimary = Boolean(t.assignedTo);
      const hasUsers =
        Array.isArray(t.assignedUsers) && t.assignedUsers.length > 0;
      return !hasPrimary && !hasUsers;
    }
    return isAssignedToUser(t, assigneeId);
  };

  const baseFiltered = tasks.filter((t) => {
    if (filter.priority && t.priority !== filter.priority) return false;
    if (filter.status && t.status !== filter.status) return false;
    if (filter.assignee && !taskMatchesAssigneeFilter(t, filter.assignee)) {
      return false;
    }
    if (
      filter.search &&
      !t.title.toLowerCase().includes(filter.search.toLowerCase())
    )
      return false;
    return true;
  });

  // Apply overdue / due-soon filter: URL param (from dashboard) or dropdown selection
  const effectiveDueFilter = dateFilter || filter.dueFilter;
  let filteredByDate = baseFiltered;
  if (effectiveDueFilter === "overdue")
    filteredByDate = baseFiltered.filter(isOverdueTask);
  if (effectiveDueFilter === "due-soon")
    filteredByDate = baseFiltered.filter(isDueSoonTask);

  const displayTasks = filteredByDate;

  // Super Admin split tables: hide done/completed/closed on My Tasks (keep them on Completed Tasks page).
  const superAdminSplitPool =
    isSuperAdmin && !showCompletedOn
      ? displayTasks.filter((t) => !isTerminalTaskStatus(t))
      : displayTasks;

  // Super Admin: assigned (incl. assignedUsers), not created by you; co-assignee tab is co-only.
  const assignedToMeTasks = isSuperAdmin
    ? superAdminSplitPool.filter(
        (t) =>
          !sameId(t.createdBy?._id, me) &&
          (sameId(t.assignedTo?._id, me) ||
            (Array.isArray(t.assignedUsers) &&
              t.assignedUsers.some((u) => sameId(u?._id ?? u, me)))),
      )
    : [];
  const createdByMeTasks = isSuperAdmin
    ? superAdminSplitPool.filter((t) => sameId(t.createdBy?._id, me))
    : [];
  const coAssigneeTasks = isSuperAdmin
    ? superAdminSplitPool.filter((t) => sameId(t.coAssignedTo?._id, me))
    : [];

  const superAdminActiveTabTasks =
    superAdminTaskTab === "assigned"
      ? assignedToMeTasks
      : superAdminTaskTab === "created"
        ? createdByMeTasks
        : coAssigneeTasks;
  const superAdminActiveTabLabelId =
    superAdminTaskTab === "assigned"
      ? "sat-tab-assigned"
      : superAdminTaskTab === "created"
        ? "sat-tab-created"
        : "sat-tab-coassignee";

  const renderSuperAdminKanbanBoard = (boardTasks) => (
    <div className="kanban-board mytasks-kanban-board-inner">
      {KANBAN_COLS.map((col) => {
        const colTasks = boardTasks.filter((t) => t.status === col.key);
        const Icon = col.icon;
        return (
          <div key={col.key} className="kanban-column">
            <div
              className="kanban-column-header"
              style={{
                background: col.bg,
                borderRadius: 12,
                padding: "8px 12px",
                margin: "0 4px 10px",
                borderBottom: `2px solid ${col.color}`,
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: 8 }}
              >
                {Icon && <Icon size={16} color={col.color} />}
                <span
                  className="kanban-column-title"
                  style={{ color: col.color }}
                >
                  {col.label}
                </span>
              </div>
              <span
                className="badge"
                style={{
                  background: "#ffffffdd",
                  color: col.color,
                  borderRadius: 999,
                  minWidth: 24,
                  textAlign: "center",
                }}
              >
                {colTasks.length}
              </span>
            </div>
            {colTasks.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "28px 0 20px",
                  color: "var(--text-light)",
                  fontSize: "0.8rem",
                }}
              >
                No tasks
              </div>
            ) : (
              colTasks.map((task) => (
                <TaskCard
                  key={task._id}
                  task={task}
                  isNew={isNewTask(task, me)}
                  canEdit={canEdit(task)}
                  canDel={canDel(task)}
                  onEdit={setEditTask}
                  onDelete={handleDelete}
                  onComment={(t) =>
                    setCommentTask({ _id: t._id, title: t.title })
                  }
                  onViewDetails={(t) => navigate(`/tasks/${t._id}`)}
                  onAssign={(t) =>
                    setQuickEdit({ task: t, type: "assign" })
                  }
                  onPriority={(t) =>
                    setQuickEdit({ task: t, type: "priority" })
                  }
                  onStatus={(t) =>
                    setQuickEdit({ task: t, type: "status" })
                  }
                  onAttach={handleAttachClick}
                />
              ))
            )}
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              style={{
                width: "100%",
                marginTop: 10,
                justifyContent: "center",
                borderStyle: "dashed",
              }}
              onClick={() => setShowModal(true)}
            >
              <MdAdd size={14} /> Add Task
            </button>
          </div>
        );
      })}
    </div>
  );

  const renderListTableRows = (taskList, rowOpts = {}) =>
    taskList.map((task) => (
      <tr key={task._id}>
        <td className="col-title" style={{ maxWidth: 220 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <Link
              to={`/tasks/${task._id}`}
              style={{
                fontWeight: 600,
                color: "var(--text)",
                textDecoration: "none",
                display: "inline-block",
                maxWidth: 190,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              className="task-list-title-link"
            >
              {task.title}
            </Link>
            {isNewTask(task, me) && (
              <span className="row-new-badge">
                <span className="badge-dot" /> New
              </span>
            )}
          </div>
          {task.tags?.length > 0 && (
            <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
              {task.tags.map((tag) => (
                <span key={tag} className="tag">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </td>
        <td>
          {rowOpts.showSelfCreatedWhenNotAssignedToOthers &&
          (!task.assignedTo ||
            sameId(task.assignedTo?._id, task.createdBy?._id)) ? (
            <span className="mytasks-self-created-pill">self created</span>
          ) : rowOpts.assigneeColumnText != null ? (
            <span className="mytasks-self-created-pill">
              {rowOpts.assigneeColumnText}
            </span>
          ) : task.assignedTo ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <UserAvatar user={task.assignedTo} size={28} />
              <span style={{ fontSize: "0.875rem", fontWeight: 600 }}>
                {task.assignedTo.name}
              </span>
            </div>
          ) : (
            <span className="text-muted">—</span>
          )}
        </td>
        <td>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <UserAvatar user={task.createdBy} size={24} />
            <span
              className="text-muted"
              style={{ fontSize: "0.85rem" }}
            >
              {task.createdBy?.name}
            </span>
          </div>
        </td>
        <td>
          <span className={`badge badge-${task.status}`}>{task.status}</span>
        </td>
        <td>
          <span className={`badge badge-${task.priority}`}>{task.priority}</span>
        </td>
        {showCompletedOn && (
          <td style={{ whiteSpace: "nowrap" }}>
            <div style={{ fontSize: "0.82rem", fontWeight: 600 }}>
              {format(
                new Date(task.updatedAt || task.createdAt),
                "MMM d, yyyy",
              )}
            </div>
            <div
              style={{
                fontSize: "0.7rem",
                color: "var(--text-muted)",
              }}
            >
              {format(
                new Date(task.updatedAt || task.createdAt),
                "hh:mm a",
              )}
            </div>
          </td>
        )}
        <td className="text-muted">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              minHeight: 20,
            }}
          >
            {task.deadline ? (
              <>
                <MdCalendarToday size={14} />{" "}
                {format(new Date(task.deadline), "MMM d, yyyy")}
              </>
            ) : (
              "—"
            )}
          </div>
        </td>
        <td>
          <div
            style={{
              display: "flex",
              gap: 4,
              alignItems: "center",
            }}
          >
            {!hideCommentIcons && task.comments?.length > 0 && (
              <span
                className="action-btn has-comments"
                title={`${task.comments.length} comment(s)`}
              >
                <MdComment size={16} />
                <span className="comment-bubble">{task.comments.length}</span>
              </span>
            )}
            <ActionMenu
              items={[
                {
                  label: "View task details",
                  icon: <MdVisibility size={16} />,
                  onClick: () => navigate(`/tasks/${task._id}`),
                },
                ...(canEdit(task)
                  ? [
                      {
                        label: "Edit task",
                        icon: <MdEdit size={16} />,
                        onClick: () => setEditTask(task),
                      },
                      {
                        label: "Assign to user",
                        icon: <MdPerson size={16} />,
                        onClick: () =>
                          setQuickEdit({
                            task,
                            type: "assign",
                          }),
                      },
                      {
                        label: "Change priority",
                        icon: <MdFlag size={16} />,
                        onClick: () =>
                          setQuickEdit({
                            task,
                            type: "priority",
                          }),
                      },
                      {
                        label: "Change status",
                        icon: <MdSwapHoriz size={16} />,
                        onClick: () =>
                          setQuickEdit({
                            task,
                            type: "status",
                          }),
                      },
                    ]
                  : []),
                {
                  label: "Add comment",
                  icon: hideCommentIcons ? null : <MdComment size={16} />,
                  onClick: () =>
                    setCommentTask({
                      _id: task._id,
                      title: task.title,
                    }),
                },
                {
                  label: "View comments",
                  icon: hideCommentIcons ? null : <MdComment size={16} />,
                  onClick: () =>
                    setCommentTask({
                      _id: task._id,
                      title: task.title,
                    }),
                },
                "divider",
                ...(canDel(task)
                  ? [
                      {
                        label: "Delete task",
                        icon: <MdDelete size={16} />,
                        onClick: () => handleDelete(task._id),
                        danger: true,
                      },
                    ]
                  : []),
              ].filter(Boolean)}
              align="right"
            />
          </div>
        </td>
      </tr>
    ));

  if (loading)
    return (
      <div className="loading">
        <div className="spinner" />
        <span className="loading-text">Loading...</span>
      </div>
    );

  return (
    <main className="page">
      <input
        ref={attachInputRef}
        type="file"
        style={{ display: "none" }}
        onChange={handleAttachChange}
      />
      {(showModal || editTask) && (
        <TaskModal
          task={editTask}
          users={users}
          currentUser={user}
          onClose={() => {
            setShowModal(false);
            setEditTask(null);
          }}
          onSaved={fetchTasks}
        />
      )}
      {commentTask && (
        <CommentModal
          taskId={commentTask._id}
          taskTitle={commentTask.title}
          onClose={() => setCommentTask(null)}
          // Refresh tasks so comment count and latest comment info update in real time
          onUpdated={fetchTasks}
        />
      )}
      {quickEdit && (
        <QuickEditModal
          task={quickEdit.task}
          type={quickEdit.type}
          users={users}
          currentUser={user}
          onClose={() => setQuickEdit(null)}
          onSaved={fetchTasks}
        />
      )}

      <div className="page-header mytasks-page-header">
        <div>
          <h1 className="page-title">
            <span className="title-icon">
              <MdTask />
            </span>
            {title}
          </h1>
          <p className="page-subtitle mytasks-page-subtitle">
            {effectiveDueFilter === "overdue" && "Overdue tasks only · "}
            {effectiveDueFilter === "due-soon" &&
              `Due within ${DUE_SOON_DAYS} days only · `}
            {isSuperAdmin ? (
              <>
                {assignedToMeTasks.length} assigned to me
                {" · "}
                {createdByMeTasks.length} created by me
                {" · "}
                {coAssigneeTasks.length} co-assignee
              </>
            ) : (
              <>
                {displayTasks.length} active task
                {displayTasks.length !== 1 ? "s" : ""}
              </>
            )}
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {!hideNewTaskButton && (
            <button
              className="btn btn-primary"
              onClick={() => setShowModal(true)}
            >
              <MdAdd size={20} /> New Task
            </button>
          )}
        </div>
      </div>

      {/* Filters + View toggle */}
      <div className="mytasks-filters-bar">
        <div className="mytasks-search-wrap">
          <MdFilterList className="search-icon" />
          <input
            style={{
              border: "none",
              outline: "none",
              padding: "10px 0",
              fontSize: "0.9rem",
              width: "100%",
              background: "transparent",
              color: "var(--text)",
            }}
            placeholder="Search tasks..."
            value={filter.search}
            onChange={(e) => setFilter({ ...filter, search: e.target.value })}
          />
        </div>
        {!lockedStatus && (
          <select
            className="form-control"
            style={{ width: "auto", minWidth: 130 }}
            value={filter.status}
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
          >
            <option value="">All Statuses</option>
            <option value="todo">To Do</option>
            <option value="in-progress">In Progress</option>
            <option value="review">Review</option>
            <option value="done">Done</option>
          </select>
        )}
        <select
          className="form-control"
          style={{ width: "auto", minWidth: 130 }}
          value={filter.priority}
          onChange={(e) => setFilter({ ...filter, priority: e.target.value })}
        >
          <option value="">All Priorities</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
          <option value="critical">Critical</option>
        </select>
        <select
          className="form-control"
          style={{ width: "auto", minWidth: 160 }}
          value={filter.assignee}
          onChange={(e) => setFilter({ ...filter, assignee: e.target.value })}
        >
          <option value="">All Assignees</option>
          <option value="unassigned">(Unassigned)</option>
          {users.map((u) => (
            <option key={u._id} value={u._id}>
              {u.name}
            </option>
          ))}
        </select>
        <select
          className="form-control"
          style={{ width: "auto", minWidth: 160 }}
          value={dateFilter || filter.dueFilter}
          onChange={(e) => {
            const v = e.target.value;
            if (dateFilter) setSearchParams({});
            setFilter((f) => ({ ...f, dueFilter: v }));
          }}
        >
          <option value="">All due dates</option>
          <option value="overdue">Overdue</option>
          <option value="due-soon">Due within {DUE_SOON_DAYS} days</option>
        </select>
        {(filter.search ||
          filter.priority ||
          filter.status ||
          filter.assignee ||
          filter.dueFilter ||
          dateFilter) && (
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => {
              setFilter({
                priority: "",
                status: "",
                assignee: "",
                dueFilter: "",
                search: "",
              });
              if (dateFilter) setSearchParams({});
            }}
          >
            Clear filters{dateFilter ? " (including date)" : ""}
          </button>
        )}
        {!forceView && (
          <div className="mytasks-view-toggle">
            <button
              onClick={() => setView("kanban")}
              className={view === "kanban" ? "active" : ""}
            >
              <MdViewKanban size={18} /> Kanban
            </button>
            <button
              onClick={() => setView("list")}
              className={view === "list" ? "active" : ""}
            >
              <MdTableRows size={18} /> List
            </button>
          </div>
        )}
      </div>

      {isSuperAdmin && (view === "kanban" || view === "list") && (
        <div
          className="mytasks-superadmin-tabs"
          role="tablist"
          aria-label="My task groups"
        >
          <button
            type="button"
            role="tab"
            id="sat-tab-assigned"
            aria-selected={superAdminTaskTab === "assigned"}
            aria-controls="sat-panel"
            className={superAdminTaskTab === "assigned" ? "active" : ""}
            onClick={() => setSuperAdminTaskTab("assigned")}
          >
            Tasks assigned to me
            <span className="mytasks-tab-count">{assignedToMeTasks.length}</span>
          </button>
          <button
            type="button"
            role="tab"
            id="sat-tab-created"
            aria-selected={superAdminTaskTab === "created"}
            aria-controls="sat-panel"
            className={superAdminTaskTab === "created" ? "active" : ""}
            onClick={() => setSuperAdminTaskTab("created")}
          >
            Tasks created by me
            <span className="mytasks-tab-count">{createdByMeTasks.length}</span>
          </button>
          <button
            type="button"
            role="tab"
            id="sat-tab-coassignee"
            aria-selected={superAdminTaskTab === "coassignee"}
            aria-controls="sat-panel"
            className={superAdminTaskTab === "coassignee" ? "active" : ""}
            onClick={() => setSuperAdminTaskTab("coassignee")}
          >
            Tasks in which I&apos;m co-assignee
            <span className="mytasks-tab-count">{coAssigneeTasks.length}</span>
          </button>
        </div>
      )}

      {/* ── Kanban View ── */}
      {view === "kanban" && isSuperAdmin && (
        <>
          {superAdminSplitPool.length === 0 ? (
            <div className="card">
              <div className="empty-state">
                <MdAdd size={48} />
                <h3>No tasks found</h3>
                <p>Create a task or adjust your filters.</p>
              </div>
            </div>
          ) : (
            <div
              id="sat-panel"
              role="tabpanel"
              aria-labelledby={superAdminActiveTabLabelId}
              className="mytasks-superadmin-tab-panel"
            >
              {superAdminActiveTabTasks.length === 0 ? (
                <div className="card">
                  <div className="empty-state mytasks-split-empty">
                    <MdTask size={40} />
                    <p>No tasks in this tab.</p>
                  </div>
                </div>
              ) : (
                renderSuperAdminKanbanBoard(superAdminActiveTabTasks)
              )}
            </div>
          )}
        </>
      )}
      {view === "kanban" && !isSuperAdmin && (
        <div className="kanban-board">
          {KANBAN_COLS.map((col) => {
            const colTasks = displayTasks.filter((t) => t.status === col.key);
            const Icon = col.icon;
            return (
              <div key={col.key} className="kanban-column">
                <div
                  className="kanban-column-header"
                  style={{
                    background: col.bg,
                    borderRadius: 12,
                    padding: "8px 12px",
                    margin: "0 4px 10px",
                    borderBottom: `2px solid ${col.color}`,
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    {Icon && <Icon size={16} color={col.color} />}
                    <span
                      className="kanban-column-title"
                      style={{ color: col.color }}
                    >
                      {col.label}
                    </span>
                  </div>
                  <span
                    className="badge"
                    style={{
                      background: "#ffffffdd",
                      color: col.color,
                      borderRadius: 999,
                      minWidth: 24,
                      textAlign: "center",
                    }}
                  >
                    {colTasks.length}
                  </span>
                </div>
                {colTasks.length === 0 ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "28px 0 20px",
                      color: "var(--text-light)",
                      fontSize: "0.8rem",
                    }}
                  >
                    No tasks
                  </div>
                ) : (
                  colTasks.map((task) => (
                    <TaskCard
                      key={task._id}
                      task={task}
                      isNew={isNewTask(task, me)}
                      canEdit={canEdit(task)}
                      canDel={canDel(task)}
                      onEdit={setEditTask}
                      onDelete={handleDelete}
                      onComment={(t) =>
                        setCommentTask({ _id: t._id, title: t.title })
                      }
                      onViewDetails={(t) => navigate(`/tasks/${t._id}`)}
                      onAssign={(t) =>
                        setQuickEdit({ task: t, type: "assign" })
                      }
                      onPriority={(t) =>
                        setQuickEdit({ task: t, type: "priority" })
                      }
                      onStatus={(t) =>
                        setQuickEdit({ task: t, type: "status" })
                      }
                      onAttach={handleAttachClick}
                    />
                  ))
                )}
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{
                    width: "100%",
                    marginTop: 10,
                    justifyContent: "center",
                    borderStyle: "dashed",
                  }}
                  onClick={() => setShowModal(true)}
                >
                  <MdAdd size={14} /> Add Task
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* ── List View ── */}
      {view === "list" && !isSuperAdmin && (
        <div className="card">
          {displayTasks.length === 0 ? (
            <div className="empty-state">
              <MdAdd size={48} />
              <h3>No tasks found</h3>
              <p>Create a task or adjust your filters.</p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="mytasks-list-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Assigned To</th>
                    <th>Created By</th>
                    <th>Status</th>
                    <th>Priority</th>
                    {showCompletedOn && <th>Completed On</th>}
                    <th>Deadline</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>{renderListTableRows(displayTasks)}</tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {view === "list" && isSuperAdmin && (
        <>
          {superAdminSplitPool.length === 0 ? (
            <div className="card">
              <div className="empty-state">
                <MdAdd size={48} />
                <h3>No tasks found</h3>
                <p>Create a task or adjust your filters.</p>
              </div>
            </div>
          ) : (
            <div
              id="sat-panel"
              role="tabpanel"
              aria-labelledby={superAdminActiveTabLabelId}
              className="card mytasks-superadmin-tab-panel"
            >
              {superAdminActiveTabTasks.length === 0 ? (
                <div className="empty-state mytasks-split-empty">
                  <MdTask size={40} />
                  <p>No tasks in this tab.</p>
                </div>
              ) : (
                <div className="table-wrapper">
                  <table className="mytasks-list-table">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Assigned To</th>
                        <th>Created By</th>
                        <th>Status</th>
                        <th>Priority</th>
                        {showCompletedOn && <th>Completed On</th>}
                        <th>Deadline</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {renderListTableRows(
                        superAdminActiveTabTasks,
                        superAdminTaskTab === "created"
                          ? { showSelfCreatedWhenNotAssignedToOthers: true }
                          : {},
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </main>
  );
};

export default MyTasks;
